// src/pages/IntelligenceLayer.tsx: PB-only client relationship intelligence.
// Five tabs: Overview | Firm Analysis | Individual Analysis | Alerts | Value Report.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { useAuth } from '../context/AuthContext';
import { useProfile } from '../hooks/useProfile';
import { supabase } from '../services/supabase';
import { card, inputStyle, labelStyle, btnPrimary, btnGold, btnGhost, Banner, LoadingScreen } from '../lib/pulseUI';
import {
  C, RangePreset, RANGE_PRESETS, DateRange, rangeForPreset, previousRange, toDateInput, fromDateInput,
  fmtDate, fmtDateTime, fmtRelative, daysSince, fmtNumber, fmtAUM,
  ActivityRow, actionMeta, CHART_CATEGORIES, bucketByDay, ActionCategory,
  AlertRule, AlertTrigger, AlertType, ALERT_TYPES, alertTypeLabel, ReportNotes,
} from '../lib/intelligence';
import {
  FirmLite, PersonLite, FirmStat, OverviewMetrics, FirmProductDetail,
  loadFirms, loadPeople, membersByFirm, loadActivity, loadOverviewMetrics, loadFirmStats,
  countByAction, loadFirmProductDetail, loadAlertRules, loadAlertTriggers, evaluateAlerts,
  loadReportNotes, saveReportNotes, isMissingTable,
} from '../lib/intelligenceQueries';

type Tab = 'overview' | 'firm' | 'individual' | 'alerts' | 'report';

const PAGE_WRAP: React.CSSProperties = { padding: '28px 32px', minHeight: '100vh', background: C.surface, fontFamily: 'system-ui, -apple-system, sans-serif' };

export default function IntelligenceLayer() {
  const { profile, loading } = useProfile();
  const [tab, setTab] = useState<Tab>('overview');

  // Shared cross-tab state.
  const [preset, setPreset] = useState<RangePreset>('this_month');
  const [customStart, setCustomStart] = useState(toDateInput(new Date(new Date().getFullYear(), new Date().getMonth(), 1)));
  const [customEnd, setCustomEnd] = useState(toDateInput(new Date()));
  const [selectedFirmId, setSelectedFirmId] = useState<string | null>(null);
  const [selectedPersonId, setSelectedPersonId] = useState<string | null>(null);

  const range: DateRange = useMemo(() =>
    preset === 'custom'
      ? { start: fromDateInput(customStart), end: fromDateInput(customEnd, true) }
      : rangeForPreset(preset),
    [preset, customStart, customEnd]);

  // Firms + people loaded once, shared across tabs.
  const [firms, setFirms] = useState<FirmLite[]>([]);
  const [people, setPeople] = useState<PersonLite[]>([]);
  const [baseErr, setBaseErr] = useState<string>('');
  const [baseLoading, setBaseLoading] = useState(true);

  useEffect(() => {
    if (!profile?.is_super_admin) return;
    let active = true;
    (async () => {
      setBaseLoading(true); setBaseErr('');
      try {
        const f = await loadFirms();
        const p = await loadPeople(f);
        if (!active) return;
        setFirms(f); setPeople(p);
      } catch (e: any) {
        if (active) setBaseErr(isMissingTable(e) ? 'MIGRATION' : (e.message || 'Failed to load data.'));
      } finally {
        if (active) setBaseLoading(false);
      }
    })();
    return () => { active = false; };
  }, [profile]);

  if (loading) return <LoadingScreen />;
  if (!profile?.is_super_admin) {
    return (
      <div style={PAGE_WRAP}>
        <Banner type="err" text="Restricted, the Intelligence Layer is available to the Altveno admin team only." />
      </div>
    );
  }

  const goToFirm = (firmId: string) => { setSelectedFirmId(firmId); setTab('firm'); };
  const goToPerson = (personId: string) => { setSelectedPersonId(personId); setTab('individual'); };

  const rangeBar = (
    <DateRangeBar preset={preset} setPreset={setPreset} customStart={customStart} setCustomStart={setCustomStart} customEnd={customEnd} setCustomEnd={setCustomEnd} />
  );

  return (
    <div style={PAGE_WRAP}>
      <div style={{ marginBottom: '18px' }} className="ilr-noprint">
        <h1 style={{ fontFamily: 'Georgia, serif', fontSize: '24px', color: C.black, fontWeight: 400, margin: '0 0 4px', letterSpacing: '-0.3px' }}>Intelligence Layer</h1>
        <p style={{ fontSize: '13px', color: C.muted, margin: 0 }}>Client relationship intelligence across the Altveno platform</p>
      </div>

      <div className="ilr-noprint" style={{ display: 'flex', gap: '2px', borderBottom: `1px solid ${C.border}`, marginBottom: '22px', flexWrap: 'wrap' }}>
        {([['overview', 'Overview'], ['firm', 'Firm Analysis'], ['individual', 'Individual Analysis'], ['alerts', 'Alerts'], ['report', 'Value Report']] as [Tab, string][]).map(([key, lbl]) => (
          <button key={key} onClick={() => setTab(key)} style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: '10px 16px', fontSize: '13px', fontFamily: 'system-ui',
            fontWeight: tab === key ? 600 : 500, color: tab === key ? C.navy : C.muted,
            borderBottom: tab === key ? `2px solid ${C.navy}` : '2px solid transparent', marginBottom: '-1px',
          }}>{lbl}</button>
        ))}
      </div>

      {baseErr === 'MIGRATION' && <MigrationNotice />}
      {baseErr && baseErr !== 'MIGRATION' && <Banner type="err" text={baseErr} />}

      {tab === 'overview' && <OverviewTab range={range} rangeBar={rangeBar} firms={firms} people={people} onFirm={goToFirm} baseLoading={baseLoading} />}
      {tab === 'firm' && <FirmTab range={range} rangeBar={rangeBar} firms={firms} people={people} selectedFirmId={selectedFirmId} setSelectedFirmId={setSelectedFirmId} onPerson={goToPerson} />}
      {tab === 'individual' && <IndividualTab range={range} rangeBar={rangeBar} people={people} selectedPersonId={selectedPersonId} setSelectedPersonId={setSelectedPersonId} />}
      {tab === 'alerts' && <AlertsTab firms={firms} />}
      {tab === 'report' && <ReportTab range={range} rangeBar={rangeBar} firms={firms} people={people} selectedFirmId={selectedFirmId} setSelectedFirmId={setSelectedFirmId} />}
    </div>
  );
}

function MigrationNotice() {
  return (
    <div style={{ ...card, padding: '22px 24px', maxWidth: '720px', borderColor: '#FDE68A', background: '#FEFCE8' }}>
      <div style={{ fontSize: '14px', fontWeight: 700, color: C.amber, marginBottom: '6px' }}>Database migration required</div>
      <div style={{ fontSize: '13px', color: C.textSec, lineHeight: 1.6 }}>
        The <code>activity_log</code> and intelligence tables don’t exist yet. Run
        <code style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '3px', padding: '1px 6px', margin: '0 4px' }}>supabase/migrations/20260720_intelligence_layer.sql</code>
        in the Supabase SQL editor, then reload this page.
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  SHARED CONTROLS
// ════════════════════════════════════════════════════════════════════════════
function DateRangeBar({ preset, setPreset, customStart, setCustomStart, customEnd, setCustomEnd }: {
  preset: RangePreset; setPreset: (p: RangePreset) => void;
  customStart: string; setCustomStart: (s: string) => void; customEnd: string; setCustomEnd: (s: string) => void;
}) {
  return (
    <div className="ilr-noprint" style={{ ...card, padding: '12px 14px', marginBottom: '18px', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
      <span style={{ fontSize: '10px', fontWeight: 700, color: C.muted, letterSpacing: '0.6px', textTransform: 'uppercase' }}>Period</span>
      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
        {RANGE_PRESETS.map(p => (
          <button key={p.value} onClick={() => setPreset(p.value)} style={{
            padding: '6px 12px', borderRadius: '3px', fontSize: '12px', cursor: 'pointer', fontFamily: 'system-ui', fontWeight: 500,
            border: `1px solid ${preset === p.value ? C.navy : C.border}`, background: preset === p.value ? C.navy : C.white, color: preset === p.value ? C.white : C.textSec,
          }}>{p.label}</button>
        ))}
      </div>
      {preset === 'custom' && (
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} style={{ ...inputStyle, width: 'auto', padding: '6px 9px', fontSize: '12px' }} />
          <span style={{ color: C.muted, fontSize: '12px' }}>to</span>
          <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} style={{ ...inputStyle, width: 'auto', padding: '6px 9px', fontSize: '12px' }} />
        </div>
      )}
    </div>
  );
}

function MetricCard({ label, value, sub, accent }: { label: string; value: React.ReactNode; sub?: string; accent?: string }) {
  return (
    <div style={{ ...card, padding: '16px 18px' }}>
      <div style={{ fontSize: '10px', fontWeight: 700, color: C.muted, letterSpacing: '0.6px', textTransform: 'uppercase', marginBottom: '8px' }}>{label}</div>
      <div style={{ fontFamily: 'Georgia, serif', fontSize: '30px', fontWeight: 600, color: accent || C.navy, lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: '11.5px', color: C.mutedLt, marginTop: '6px' }}>{sub}</div>}
    </div>
  );
}

function Trend({ cur, prev }: { cur: number; prev: number }) {
  if (cur === prev) return <span style={{ color: C.mutedLt, fontSize: '12px' }}>→</span>;
  const up = cur > prev;
  return <span title={`${prev} previous period`} style={{ color: up ? C.success : C.danger, fontSize: '12px', fontWeight: 700 }}>{up ? '▲' : '▼'}</span>;
}

// Searchable firm dropdown.
function FirmSelect({ firms, value, onChange, placeholder }: { firms: FirmLite[]; value: string | null; onChange: (id: string) => void; placeholder?: string }) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selected = firms.find(f => f.id === value);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return firms.filter(f => !q || f.name.toLowerCase().includes(q) || (f.headquarters || '').toLowerCase().includes(q)).slice(0, 60);
  }, [firms, search]);
  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', h); return () => document.removeEventListener('mousedown', h);
  }, []);
  return (
    <div ref={ref} style={{ position: 'relative', maxWidth: '360px' }}>
      <button onClick={() => setOpen(o => !o)} style={{ ...inputStyle, textAlign: 'left', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ color: selected ? C.text : C.mutedLt }}>{selected ? selected.name : (placeholder || 'Select a firm…')}</span>
        <span style={{ color: C.mutedLt, fontSize: '10px' }}>▼</span>
      </button>
      {open && (
        <div style={{ ...card, position: 'absolute', top: '42px', left: 0, right: 0, zIndex: 20, padding: '8px', maxHeight: '320px', overflowY: 'auto' }}>
          <input autoFocus value={search} onChange={e => setSearch(e.target.value)} placeholder="Search firms…" style={{ ...inputStyle, padding: '7px 10px', fontSize: '12.5px', marginBottom: '6px' }} />
          {filtered.map(f => (
            <button key={f.id} onClick={() => { onChange(f.id); setOpen(false); setSearch(''); }} style={{
              display: 'flex', width: '100%', textAlign: 'left', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '7px 9px', borderRadius: '3px',
              border: 'none', background: f.id === value ? 'rgba(10,22,40,0.04)' : 'transparent', fontFamily: 'system-ui',
            }} onMouseEnter={e => (e.currentTarget.style.background = C.surface)} onMouseLeave={e => (e.currentTarget.style.background = f.id === value ? 'rgba(10,22,40,0.04)' : 'transparent')}>
              <span style={{ fontSize: '12.5px', color: C.text, fontWeight: 500, flex: 1 }}>{f.name}</span>
              <RoleTag role={f.role} />
            </button>
          ))}
          {!filtered.length && <div style={{ fontSize: '12px', color: C.mutedLt, padding: '10px', textAlign: 'center' }}>No firms found.</div>}
        </div>
      )}
    </div>
  );
}

function PersonSelect({ people, value, onChange }: { people: PersonLite[]; value: string | null; onChange: (id: string) => void }) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selected = people.find(p => p.id === value);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return people.filter(p => !q || [p.full_name, p.email, p.firmName].filter(Boolean).some(v => String(v).toLowerCase().includes(q))).slice(0, 60);
  }, [people, search]);
  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', h); return () => document.removeEventListener('mousedown', h);
  }, []);
  return (
    <div ref={ref} style={{ position: 'relative', maxWidth: '360px' }}>
      <button onClick={() => setOpen(o => !o)} style={{ ...inputStyle, textAlign: 'left', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ color: selected ? C.text : C.mutedLt }}>{selected ? (selected.full_name || selected.email) : 'Select an individual…'}</span>
        <span style={{ color: C.mutedLt, fontSize: '10px' }}>▼</span>
      </button>
      {open && (
        <div style={{ ...card, position: 'absolute', top: '42px', left: 0, right: 0, zIndex: 20, padding: '8px', maxHeight: '320px', overflowY: 'auto' }}>
          <input autoFocus value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or email…" style={{ ...inputStyle, padding: '7px 10px', fontSize: '12.5px', marginBottom: '6px' }} />
          {filtered.map(p => (
            <button key={p.id} onClick={() => { onChange(p.id); setOpen(false); setSearch(''); }} style={{
              display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer', padding: '7px 9px', borderRadius: '3px',
              border: 'none', background: p.id === value ? 'rgba(10,22,40,0.04)' : 'transparent', fontFamily: 'system-ui',
            }} onMouseEnter={e => (e.currentTarget.style.background = C.surface)} onMouseLeave={e => (e.currentTarget.style.background = p.id === value ? 'rgba(10,22,40,0.04)' : 'transparent')}>
              <div style={{ fontSize: '12.5px', color: C.text, fontWeight: 500 }}>{p.full_name || p.email}</div>
              <div style={{ fontSize: '11px', color: C.muted }}>{p.firmName || 'No firm'} · <span style={{ textTransform: 'capitalize' }}>{p.role}</span></div>
            </button>
          ))}
          {!filtered.length && <div style={{ fontSize: '12px', color: C.mutedLt, padding: '10px', textAlign: 'center' }}>No people found.</div>}
        </div>
      )}
    </div>
  );
}

function RoleTag({ role }: { role: string }) {
  const map: Record<string, { bg: string; fg: string; bd: string }> = {
    Manager:   { bg: '#EFF6FF', fg: C.teal, bd: '#BFDBFE' },
    Allocator: { bg: 'rgba(201,168,76,0.12)', fg: C.amber, bd: 'rgba(201,168,76,0.35)' },
    Firm:      { bg: C.surface, fg: C.muted, bd: C.border },
  };
  const s = map[role] || map.Firm;
  return <span style={{ background: s.bg, color: s.fg, border: `1px solid ${s.bd}`, fontSize: '9px', fontWeight: 700, padding: '2px 7px', borderRadius: '2px', letterSpacing: '0.4px', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{role}</span>;
}

function SectionTitle({ children, right }: { children: React.ReactNode; right?: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '26px 0 12px' }}>
      <span style={{ fontSize: '13px', fontWeight: 700, color: C.navy, letterSpacing: '0.2px' }}>{children}</span>
      {right}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  TAB 1, OVERVIEW
// ════════════════════════════════════════════════════════════════════════════
function OverviewTab({ range, rangeBar, firms, people, onFirm, baseLoading }: {
  range: DateRange; rangeBar: React.ReactNode; firms: FirmLite[]; people: PersonLite[]; onFirm: (id: string) => void; baseLoading: boolean;
}) {
  const [metrics, setMetrics] = useState<OverviewMetrics | null>(null);
  const [rows, setRows] = useState<ActivityRow[]>([]);
  const [stats, setStats] = useState<FirmStat[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [hidden, setHidden] = useState<Record<string, boolean>>({});

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true); setErr('');
      try {
        const [m, r, s] = await Promise.all([
          loadOverviewMetrics(range),
          loadActivity(range, { limit: 6000 }),
          loadFirmStats(range, firms),
        ]);
        if (!active) return;
        setMetrics(m); setRows(r); setStats(s.stats);
      } catch (e: any) {
        if (active) setErr(isMissingTable(e) ? '' : (e.message || 'Failed to load overview.'));
      } finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [range, firms]);

  const chartData = useMemo(() => bucketByDay(rows, range), [rows, range]);
  const emailFor = useMemo(() => {
    const m = new Map<string, string>();
    people.forEach(p => { if (p.firmId && p.email && !m.has(p.firmId)) m.set(p.firmId, p.email); });
    return m;
  }, [people]);

  const leaderboard = useMemo(() => [...stats].filter(s => s.actionsInPeriod > 0).sort((a, b) => b.actionsInPeriod - a.actionsInPeriod).slice(0, 12), [stats]);
  const atRisk = useMemo(() => stats
    .map(s => ({ ...s, inactiveDays: s.lastActive ? daysSince(s.lastActive) : null }))
    .filter(s => s.inactiveDays === null || s.inactiveDays >= 30)
    .sort((a, b) => (b.inactiveDays ?? 99999) - (a.inactiveDays ?? 99999))
    .slice(0, 15), [stats]);

  if (loading || baseLoading) return <div style={{ color: C.muted, fontSize: '13px' }}>Loading overview…</div>;

  return (
    <div>
      {rangeBar}
      {err && <Banner type="err" text={err} />}

      {/* Metric cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '12px' }}>
        <MetricCard label="Active Firms" value={fmtNumber(metrics?.activeFirms || 0)} sub="with activity this period" />
        <MetricCard label="Connections Made" value={fmtNumber(metrics?.connections || 0)} />
        <MetricCard label="Event Introductions" value={fmtNumber(metrics?.eventIntros || 0)} sub="scheduled meetings" />
        <MetricCard label="AI Queries" value={fmtNumber(metrics?.aiQueries || 0)} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
        <MetricCard label="Messages Sent" value={fmtNumber(metrics?.messages || 0)} />
        <MetricCard label="DDQs Submitted" value={fmtNumber(metrics?.ddqs || 0)} />
        <MetricCard label="Documents Accessed" value={fmtNumber(metrics?.docsAccessed || 0)} />
        <MetricCard label="Survey Completions" value={fmtNumber(metrics?.surveys || 0)} />
      </div>

      {/* Platform activity chart */}
      <SectionTitle>Platform Activity</SectionTitle>
      <div style={{ ...card, padding: '18px 18px 8px', height: '320px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 6, right: 12, left: -18, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={C.borderLt} vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 10.5, fill: C.muted }} axisLine={{ stroke: C.border }} tickLine={false} minTickGap={20} />
            <YAxis allowDecimals={false} tick={{ fontSize: 10.5, fill: C.muted }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={{ background: C.navy, border: 'none', borderRadius: '4px', fontSize: '12px' }} labelStyle={{ color: C.white }} itemStyle={{ color: C.white }} />
            <Legend onClick={(e: any) => setHidden(h => ({ ...h, [e.dataKey]: !h[e.dataKey] }))} wrapperStyle={{ fontSize: '12px', cursor: 'pointer' }} />
            {CHART_CATEGORIES.map(c => (
              <Line key={c.key} type="monotone" dataKey={c.key} name={c.label} stroke={c.color} strokeWidth={2} dot={false} hide={hidden[c.key]} activeDot={{ r: 4 }} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div style={{ fontSize: '11px', color: C.mutedLt, marginTop: '6px' }}>Click a legend label to toggle a series.</div>

      {/* Most active firms */}
      <SectionTitle>Most Active Firms</SectionTitle>
      <div style={{ ...card, overflow: 'hidden' }}>
        {leaderboard.length === 0 ? (
          <div style={{ padding: '28px', textAlign: 'center', fontSize: '13px', color: C.mutedLt }}>No firm activity in this period.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'system-ui' }}>
            <thead>
              <tr style={{ background: C.surface }}>
                {['#', 'Firm', 'Role', 'Actions', 'Last Active', 'Trend'].map((h, i) => (
                  <th key={h} style={{ textAlign: i >= 3 ? 'right' : 'left', padding: '9px 14px', fontSize: '10px', color: C.muted, fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', borderBottom: `1px solid ${C.border}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {leaderboard.map((s, i) => (
                <tr key={s.firm.id} style={{ borderBottom: `1px solid ${C.borderLt}` }}>
                  <td style={{ padding: '10px 14px', fontSize: '12px', color: C.mutedLt, fontFamily: 'Georgia, serif' }}>{i + 1}</td>
                  <td style={{ padding: '10px 14px' }}>
                    <button onClick={() => onFirm(s.firm.id)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: C.navy, fontWeight: 600, fontSize: '13px', fontFamily: 'system-ui', textAlign: 'left' }}>{s.firm.name}</button>
                  </td>
                  <td style={{ padding: '10px 14px' }}><RoleTag role={s.firm.role} /></td>
                  <td style={{ padding: '10px 14px', textAlign: 'right', fontSize: '13px', fontWeight: 700, color: C.text, fontVariantNumeric: 'tabular-nums' }}>{fmtNumber(s.actionsInPeriod)}</td>
                  <td style={{ padding: '10px 14px', textAlign: 'right', fontSize: '12px', color: C.muted }}>{fmtRelative(s.lastActive)}</td>
                  <td style={{ padding: '10px 14px', textAlign: 'right' }}><Trend cur={s.actionsInPeriod} prev={s.prevActions} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* At-risk firms */}
      <SectionTitle right={<span style={{ fontSize: '11px', color: C.mutedLt }}>Zero activity in the last 30 days</span>}>At-Risk Firms</SectionTitle>
      <div style={{ ...card, overflow: 'hidden' }}>
        {atRisk.length === 0 ? (
          <div style={{ padding: '28px', textAlign: 'center', fontSize: '13px', color: C.success }}>No at-risk firms, every firm has been active recently.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'system-ui' }}>
            <thead>
              <tr style={{ background: C.surface }}>
                {['Firm', 'Role', 'Last Activity', 'Days Inactive', ''].map((h, i) => (
                  <th key={h + i} style={{ textAlign: i === 3 ? 'right' : 'left', padding: '9px 14px', fontSize: '10px', color: C.muted, fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', borderBottom: `1px solid ${C.border}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {atRisk.map(s => (
                <tr key={s.firm.id} style={{ borderBottom: `1px solid ${C.borderLt}` }}>
                  <td style={{ padding: '10px 14px' }}>
                    <button onClick={() => onFirm(s.firm.id)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: C.navy, fontWeight: 600, fontSize: '13px', fontFamily: 'system-ui', textAlign: 'left' }}>{s.firm.name}</button>
                  </td>
                  <td style={{ padding: '10px 14px' }}><RoleTag role={s.firm.role} /></td>
                  <td style={{ padding: '10px 14px', fontSize: '12px', color: C.muted }}>{s.lastActive ? fmtDate(s.lastActive) : 'No recorded activity'}</td>
                  <td style={{ padding: '10px 14px', textAlign: 'right', fontSize: '13px', fontWeight: 700, color: C.danger, fontVariantNumeric: 'tabular-nums' }}>{s.inactiveDays === null ? '-' : s.inactiveDays}</td>
                  <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                    <a href={`mailto:${emailFor.get(s.firm.id) || ''}?subject=${encodeURIComponent('Checking in from Altveno')}`} style={{ ...btnGhost, textDecoration: 'none', display: 'inline-block', padding: '5px 12px', fontSize: '11.5px' }}>Contact</a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  PRODUCT CARDS (shared by Firm + Individual tabs)
// ════════════════════════════════════════════════════════════════════════════
function ProductCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ ...card, padding: '16px 18px' }}>
      <div style={{ fontSize: '11px', fontWeight: 700, color: C.gold, letterSpacing: '0.6px', textTransform: 'uppercase', marginBottom: '12px' }}>{title}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>{children}</div>
    </div>
  );
}
function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
      <span style={{ fontSize: '12.5px', color: C.textSec }}>{label}</span>
      <span style={{ fontSize: '13px', fontWeight: 700, color: C.navy, fontVariantNumeric: 'tabular-nums' }}>{value}</span>
    </div>
  );
}

const STAGE_LABELS: Record<string, string> = {
  intro_meeting_scheduled: 'Intro meeting', idd: 'IDD', odd: 'ODD', ddq_sent: 'DDQ sent',
  terms_negotiation: 'Terms', invested: 'Invested', declined: 'Declined', on_hold: 'On hold',
};

// The six product cards. `detail` present → firm mode (source-enriched); when absent
// (individual mode) cards fall back to activity counts only.
function ProductGrid({ counts, detail }: { counts: Record<string, number>; detail?: FirmProductDetail | null }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '12px' }}>
      <ProductCard title="AltConnect">
        <Stat label="Connections requested" value={counts['connection_requested'] || 0} />
        <Stat label="Connections approved" value={detail ? detail.connectionsApproved : (counts['connection_approved'] || 0)} />
        <Stat label="Pipeline stages advanced" value={counts['pipeline_stage_updated'] || 0} />
        {detail && detail.pipelineStages.length > 0 && (
          <div style={{ fontSize: '11.5px', color: C.muted, lineHeight: 1.6, paddingTop: '2px' }}>
            {detail.pipelineStages.map(s => `${STAGE_LABELS[s.stage] || s.stage} (${s.count})`).join(' · ')}
          </div>
        )}
        <Stat label="Investments made" value={detail ? detail.invested : (counts['investment'] || 0)} />
      </ProductCard>

      <ProductCard title="EventConnect">
        <Stat label="Events registered" value={detail ? detail.eventsRegistered : (counts['event_registered'] || 0)} />
        <Stat label="Investor meetings" value={detail ? detail.eventMeetings : '-'} />
        <Stat label="Post-event connections" value={counts['connection_requested'] || 0} />
      </ProductCard>

      <ProductCard title="TalentConnect">
        <Stat label="Jobs posted" value={detail ? detail.jobsPosted : (counts['job_posted'] || 0)} />
        <Stat label="Candidates connected" value={detail ? detail.candidatesConnected : (counts['talent_connection_made'] || 0)} />
        <Stat label="Interviews arranged" value={detail ? detail.interviews : '-'} />
        <Stat label="Hires made" value={detail ? detail.hires : '-'} />
      </ProductCard>

      <ProductCard title="ServiceConnect">
        <Stat label="Provider connections" value={detail ? detail.serviceConnections : (counts['service_connection_made'] || 0)} />
        <Stat label="Categories" value={detail ? (detail.serviceCategories.length || 0) : '-'} />
        {detail && detail.serviceCategories.length > 0 && (
          <div style={{ fontSize: '11.5px', color: C.muted, lineHeight: 1.5 }}>{detail.serviceCategories.join(', ')}</div>
        )}
      </ProductCard>

      <ProductCard title="PulseConnect">
        <Stat label="Surveys completed" value={detail ? detail.surveysCompleted : (counts['survey_completed'] || 0)} />
        {detail && <Stat label="Surveys assigned" value={detail.surveysAssigned} />}
        {detail && <Stat label="Response rate" value={detail.surveysAssigned ? `${Math.round((detail.surveysCompleted / detail.surveysAssigned) * 100)}%` : '-'} />}
        {detail && <Stat label="Last completed" value={detail.lastSurveyAt ? fmtDate(detail.lastSurveyAt) : '-'} />}
      </ProductCard>

      <ProductCard title="DDQ & Data Room">
        <Stat label="DDQs submitted" value={detail ? detail.ddqSubmitted : (counts['ddq_submitted'] || 0)} />
        <Stat label="Documents uploaded" value={detail ? detail.docsUploaded : (counts['document_uploaded'] || 0)} />
        <Stat label="Docs accessed by allocators" value={detail ? detail.docsAccessedByAllocators : (counts['document_accessed'] || 0)} />
        {detail && <Stat label="Unique allocators" value={detail.uniqueAllocatorsAccessed} />}
      </ProductCard>

      <ProductCard title="AI Usage">
        <Stat label="Total AI queries" value={counts['ai_query_made'] || 0} />
      </ProductCard>
    </div>
  );
}

// Paginated chronological timeline of activity rows.
function ActivityTimeline({ rows, nameFor }: { rows: ActivityRow[]; nameFor?: (profileId: string | null) => string }) {
  const [page, setPage] = useState(0);
  const perPage = 20;
  useEffect(() => { setPage(0); }, [rows]);
  const pages = Math.max(1, Math.ceil(rows.length / perPage));
  const slice = rows.slice(page * perPage, page * perPage + perPage);
  if (!rows.length) return <div style={{ ...card, padding: '28px', textAlign: 'center', fontSize: '13px', color: C.mutedLt }}>No activity in this period.</div>;
  return (
    <div>
      <div style={{ ...card, overflow: 'hidden' }}>
        {slice.map((r, i) => {
          const meta = actionMeta(r.action_type);
          const md = r.metadata || {};
          const entity = md.manager_firm_name || md.allocator_firm_name || md.event_name || md.candidate_name || md.job_title || md.provider_name || md.survey_title || md.file_name || md.fund_name || (md.stage ? (STAGE_LABELS[md.stage] || md.stage) : '') || '';
          return (
            <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 16px', borderBottom: i < slice.length - 1 ? `1px solid ${C.borderLt}` : 'none' }}>
              <span style={{ width: '26px', height: '26px', flexShrink: 0, borderRadius: '50%', background: `${meta.color}14`, color: meta.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: 700 }}>{meta.icon}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '12.5px', color: C.text, fontWeight: 500 }}>
                  {meta.label}{entity && <span style={{ color: C.muted, fontWeight: 400 }}>, {String(entity)}</span>}
                </div>
                {nameFor && <div style={{ fontSize: '11px', color: C.mutedLt }}>{nameFor(r.profile_id)}</div>}
              </div>
              <span style={{ fontSize: '11px', color: C.mutedLt, whiteSpace: 'nowrap' }}>{fmtDateTime(r.created_at)}</span>
            </div>
          );
        })}
      </div>
      {pages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '10px', justifyContent: 'flex-end' }}>
          <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} style={{ ...btnGhost, padding: '5px 12px', opacity: page === 0 ? 0.5 : 1 }}>Prev</button>
          <span style={{ fontSize: '12px', color: C.muted }}>Page {page + 1} of {pages}</span>
          <button onClick={() => setPage(p => Math.min(pages - 1, p + 1))} disabled={page >= pages - 1} style={{ ...btnGhost, padding: '5px 12px', opacity: page >= pages - 1 ? 0.5 : 1 }}>Next</button>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  TAB 2, FIRM ANALYSIS
// ════════════════════════════════════════════════════════════════════════════
function FirmTab({ range, rangeBar, firms, people, selectedFirmId, setSelectedFirmId, onPerson }: {
  range: DateRange; rangeBar: React.ReactNode; firms: FirmLite[]; people: PersonLite[];
  selectedFirmId: string | null; setSelectedFirmId: (id: string) => void; onPerson: (id: string) => void;
}) {
  const firm = firms.find(f => f.id === selectedFirmId) || null;
  const memberIds = useMemo(() => people.filter(p => p.firmId === selectedFirmId).map(p => p.id), [people, selectedFirmId]);
  const memberById = useMemo(() => new Map(people.map(p => [p.id, p])), [people]);

  const [rows, setRows] = useState<ActivityRow[]>([]);
  const [detail, setDetail] = useState<FirmProductDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!firm) { setRows([]); setDetail(null); return; }
    let active = true;
    (async () => {
      setLoading(true); setErr('');
      try {
        const [r, d] = await Promise.all([
          loadActivity(range, memberIds.length ? { profileIds: memberIds } : { firmId: firm.id }),
          loadFirmProductDetail(firm.id, memberIds, range),
        ]);
        if (!active) return;
        setRows(r); setDetail(d);
      } catch (e: any) { if (active) setErr(e.message || 'Failed to load firm analysis.'); }
      finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [firm, range, memberIds]);

  const counts = useMemo(() => countByAction(rows), [rows]);

  // Per-person breakdown within the firm.
  const perPerson = useMemo(() => {
    const m = new Map<string, { messages: number; connections: number; events: number; total: number; last: string | null }>();
    rows.forEach(r => {
      if (!r.profile_id) return;
      const e = m.get(r.profile_id) || { messages: 0, connections: 0, events: 0, total: 0, last: null };
      e.total += 1;
      if (r.action_type === 'message_sent') e.messages += 1;
      if (r.action_type.startsWith('connection')) e.connections += 1;
      if (r.action_type === 'event_registered') e.events += 1;
      if (!e.last || r.created_at > e.last) e.last = r.created_at;
      m.set(r.profile_id, e);
    });
    return memberIds.map(id => ({ person: memberById.get(id)!, ...(m.get(id) || { messages: 0, connections: 0, events: 0, total: 0, last: null }) }))
      .filter(x => x.person)
      .sort((a, b) => b.total - a.total);
  }, [rows, memberIds, memberById]);

  const lastActive = rows[0]?.created_at || null;

  return (
    <div>
      <div className="ilr-noprint" style={{ ...card, padding: '14px 18px', marginBottom: '16px' }}>
        <label style={labelStyle}>Firm</label>
        <FirmSelect firms={firms} value={selectedFirmId} onChange={setSelectedFirmId} />
      </div>
      {rangeBar}
      {err && <Banner type="err" text={err} />}

      {!firm ? (
        <div style={{ ...card, padding: '48px', textAlign: 'center', fontSize: '13px', color: C.muted }}>Select a firm to see its full activity breakdown.</div>
      ) : loading ? (
        <div style={{ color: C.muted, fontSize: '13px' }}>Loading firm analysis…</div>
      ) : (
        <div>
          {/* Header card */}
          <div style={{ ...card, padding: '20px 22px', marginBottom: '4px', display: 'flex', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                <span style={{ fontFamily: 'Georgia, serif', fontSize: '22px', color: C.black }}>{firm.name}</span>
                <RoleTag role={firm.role} />
              </div>
              <div style={{ fontSize: '12.5px', color: C.muted, display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                <span>{firm.type || '-'}</span>
                <span>{firm.headquarters || 'HQ unknown'}</span>
                <span>AUM {fmtAUM(firm.aum_min, firm.aum_max, firm.aum_exact)}</span>
                <span>Member since {fmtDate(firm.created_at)}</span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '28px' }}>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontFamily: 'Georgia, serif', fontSize: '26px', color: C.navy, fontWeight: 600 }}>{fmtNumber(rows.length)}</div>
                <div style={{ fontSize: '10.5px', color: C.muted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Actions this period</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontFamily: 'Georgia, serif', fontSize: '16px', color: C.textSec, fontWeight: 600, marginTop: '8px' }}>{fmtRelative(lastActive)}</div>
                <div style={{ fontSize: '10.5px', color: C.muted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Last active</div>
              </div>
            </div>
          </div>

          <SectionTitle>Activity Summary</SectionTitle>
          <ProductGrid counts={counts} detail={detail} />

          {/* Value delivered box */}
          <div style={{ ...card, marginTop: '20px', padding: '22px 24px', background: C.navy, borderColor: C.navy }}>
            <div style={{ fontSize: '11px', color: C.gold, letterSpacing: '0.8px', textTransform: 'uppercase', fontWeight: 700, marginBottom: '14px' }}>
              In this period, Altveno has delivered for {firm.name}:
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '18px' }}>
              {[
                [detail ? detail.connectionsApproved + (counts['connection_requested'] || 0) : (counts['connection_requested'] || 0), 'investor introductions'],
                [detail ? detail.eventsRegistered : 0, 'events attended'],
                [counts['ai_query_made'] || 0, 'AI insights generated'],
                [detail ? detail.docsAccessedByAllocators : 0, 'documents shared with allocators'],
                [counts['pipeline_stage_updated'] || 0, 'pipeline stages advanced'],
              ].map(([n, l], i) => (
                <div key={i}>
                  <div style={{ fontFamily: 'Georgia, serif', fontSize: '30px', color: C.gold, fontWeight: 600, lineHeight: 1 }}>{fmtNumber(Number(n))}</div>
                  <div style={{ fontSize: '11.5px', color: 'rgba(255,255,255,0.72)', marginTop: '4px' }}>{l}</div>
                </div>
              ))}
            </div>
          </div>

          <SectionTitle>Activity Timeline</SectionTitle>
          <ActivityTimeline rows={rows} nameFor={pid => (pid && memberById.get(pid)?.full_name) || 'Unknown'} />

          <SectionTitle>Individual Breakdown</SectionTitle>
          <div style={{ ...card, overflow: 'hidden' }}>
            {perPerson.length === 0 ? (
              <div style={{ padding: '24px', textAlign: 'center', fontSize: '13px', color: C.mutedLt }}>No individuals mapped to this firm.</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'system-ui' }}>
                <thead>
                  <tr style={{ background: C.surface }}>
                    {['Name', 'Role', 'Total', 'Connections', 'Messages', 'Events', 'Last Active'].map((h, i) => (
                      <th key={h} style={{ textAlign: i >= 2 && i <= 5 ? 'right' : 'left', padding: '9px 14px', fontSize: '10px', color: C.muted, fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', borderBottom: `1px solid ${C.border}` }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {perPerson.map(x => (
                    <tr key={x.person.id} style={{ borderBottom: `1px solid ${C.borderLt}` }}>
                      <td style={{ padding: '10px 14px' }}>
                        <button onClick={() => onPerson(x.person.id)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: C.navy, fontWeight: 600, fontSize: '12.5px', fontFamily: 'system-ui', textAlign: 'left' }}>{x.person.full_name || x.person.email}</button>
                      </td>
                      <td style={{ padding: '10px 14px', fontSize: '12px', color: C.muted, textTransform: 'capitalize' }}>{x.person.role}</td>
                      <td style={{ padding: '10px 14px', textAlign: 'right', fontSize: '12.5px', fontWeight: 700, color: C.text, fontVariantNumeric: 'tabular-nums' }}>{x.total}</td>
                      <td style={{ padding: '10px 14px', textAlign: 'right', fontSize: '12.5px', color: C.textSec, fontVariantNumeric: 'tabular-nums' }}>{x.connections}</td>
                      <td style={{ padding: '10px 14px', textAlign: 'right', fontSize: '12.5px', color: C.textSec, fontVariantNumeric: 'tabular-nums' }}>{x.messages}</td>
                      <td style={{ padding: '10px 14px', textAlign: 'right', fontSize: '12.5px', color: C.textSec, fontVariantNumeric: 'tabular-nums' }}>{x.events}</td>
                      <td style={{ padding: '10px 14px', fontSize: '12px', color: C.muted }}>{fmtRelative(x.last)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  TAB 3, INDIVIDUAL ANALYSIS
// ════════════════════════════════════════════════════════════════════════════
function IndividualTab({ range, rangeBar, people, selectedPersonId, setSelectedPersonId }: {
  range: DateRange; rangeBar: React.ReactNode; people: PersonLite[]; selectedPersonId: string | null; setSelectedPersonId: (id: string) => void;
}) {
  const person = people.find(p => p.id === selectedPersonId) || null;
  const [rows, setRows] = useState<ActivityRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!person) { setRows([]); return; }
    let active = true;
    (async () => {
      setLoading(true); setErr('');
      try {
        const r = await loadActivity(range, { profileId: person.id });
        if (active) setRows(r);
      } catch (e: any) { if (active) setErr(e.message || 'Failed to load individual analysis.'); }
      finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [person, range]);

  const counts = useMemo(() => countByAction(rows), [rows]);

  return (
    <div>
      <div className="ilr-noprint" style={{ ...card, padding: '14px 18px', marginBottom: '16px' }}>
        <label style={labelStyle}>Individual</label>
        <PersonSelect people={people} value={selectedPersonId} onChange={setSelectedPersonId} />
      </div>
      {rangeBar}
      {err && <Banner type="err" text={err} />}

      {!person ? (
        <div style={{ ...card, padding: '48px', textAlign: 'center', fontSize: '13px', color: C.muted }}>Select an individual to see their activity.</div>
      ) : loading ? (
        <div style={{ color: C.muted, fontSize: '13px' }}>Loading…</div>
      ) : (
        <div>
          <div style={{ ...card, padding: '20px 22px', marginBottom: '4px', display: 'flex', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontFamily: 'Georgia, serif', fontSize: '22px', color: C.black, marginBottom: '6px' }}>{person.full_name || person.email}</div>
              <div style={{ fontSize: '12.5px', color: C.muted, display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                <span>{person.firmName || 'No firm'}</span>
                <span style={{ textTransform: 'capitalize' }}>{person.role}</span>
                <span>{person.email}</span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '28px' }}>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontFamily: 'Georgia, serif', fontSize: '26px', color: C.navy, fontWeight: 600 }}>{fmtNumber(rows.length)}</div>
                <div style={{ fontSize: '10.5px', color: C.muted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Actions this period</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontFamily: 'Georgia, serif', fontSize: '16px', color: C.textSec, fontWeight: 600, marginTop: '8px' }}>{fmtRelative(rows[0]?.created_at || null)}</div>
                <div style={{ fontSize: '10.5px', color: C.muted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Last active</div>
              </div>
            </div>
          </div>

          <SectionTitle>Action Breakdown</SectionTitle>
          <ProductGrid counts={counts} />

          <SectionTitle>Activity Timeline</SectionTitle>
          <ActivityTimeline rows={rows} />
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  TAB 4, ALERTS
// ════════════════════════════════════════════════════════════════════════════
function AlertsTab({ firms }: { firms: FirmLite[] }) {
  const { user } = useAuth();
  const [rules, setRules] = useState<AlertRule[]>([]);
  const [triggers, setTriggers] = useState<AlertTrigger[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [checking, setChecking] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [filter, setFilter] = useState<'all' | 'unreviewed' | 'reviewed'>('all');
  const firmName = useMemo(() => new Map(firms.map(f => [f.id, f.name])), [firms]);

  async function load() {
    setLoading(true); setErr('');
    try {
      const [r, t] = await Promise.all([loadAlertRules(), loadAlertTriggers()]);
      setRules(r); setTriggers(t);
    } catch (e: any) { setErr(isMissingTable(e) ? '' : (e.message || 'Failed to load alerts.')); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function toggleRule(rule: AlertRule) {
    await supabase.from('intelligence_alerts').update({ is_active: !rule.is_active }).eq('id', rule.id);
    load();
  }
  async function deleteRule(rule: AlertRule) {
    if (!window.confirm('Delete this alert rule and its triggers?')) return;
    await supabase.from('intelligence_alerts').delete().eq('id', rule.id);
    load();
  }
  async function markReviewed(t: AlertTrigger) {
    await supabase.from('intelligence_alert_triggers').update({ is_reviewed: true }).eq('id', t.id);
    load();
  }
  async function runCheck() {
    setChecking(true); setMsg(''); setErr('');
    try {
      const n = await evaluateAlerts(firms);
      setMsg(`${n} new alert${n !== 1 ? 's' : ''} triggered.`);
      await load();
    } catch (e: any) { setErr(e.message || 'Alert check failed.'); }
    finally { setChecking(false); }
  }

  const shownTriggers = triggers.filter(t => filter === 'all' || (filter === 'reviewed' ? t.is_reviewed : !t.is_reviewed));

  if (loading) return <div style={{ color: C.muted, fontSize: '13px' }}>Loading alerts…</div>;

  return (
    <div style={{ maxWidth: '1000px' }}>
      {err && <Banner type="err" text={err} />}
      {msg && <Banner type="ok" text={msg} />}

      <SectionTitle right={
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={runCheck} disabled={checking} style={btnGold}>{checking ? 'Checking…' : 'Check Alerts'}</button>
          <button onClick={() => setShowAdd(true)} style={btnPrimary}>+ Add Alert Rule</button>
        </div>
      }>Active Alert Rules</SectionTitle>

      <div style={{ ...card, overflow: 'hidden' }}>
        {rules.length === 0 ? (
          <div style={{ padding: '28px', textAlign: 'center', fontSize: '13px', color: C.mutedLt }}>No alert rules configured yet.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'system-ui' }}>
            <thead>
              <tr style={{ background: C.surface }}>
                {['Type', 'Firm', 'Threshold', 'Active', 'Last Triggered', ''].map((h, i) => (
                  <th key={h + i} style={{ textAlign: 'left', padding: '9px 14px', fontSize: '10px', color: C.muted, fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', borderBottom: `1px solid ${C.border}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rules.map(r => (
                <tr key={r.id} style={{ borderBottom: `1px solid ${C.borderLt}` }}>
                  <td style={{ padding: '10px 14px', fontSize: '12.5px', color: C.text, fontWeight: 500 }}>{alertTypeLabel(r.alert_type)}</td>
                  <td style={{ padding: '10px 14px', fontSize: '12.5px', color: C.textSec }}>{r.firm_id ? (firmName.get(r.firm_id) || 'Unknown') : 'All firms'}</td>
                  <td style={{ padding: '10px 14px', fontSize: '12.5px', color: C.textSec }}>{r.threshold_value ?? '-'}</td>
                  <td style={{ padding: '10px 14px' }}>
                    <button onClick={() => toggleRule(r)} style={{ cursor: 'pointer', border: 'none', borderRadius: '20px', width: '38px', height: '20px', background: r.is_active ? C.success : C.mutedLt, position: 'relative', transition: 'background 0.15s' }}>
                      <span style={{ position: 'absolute', top: '2px', left: r.is_active ? '20px' : '2px', width: '16px', height: '16px', borderRadius: '50%', background: C.white, transition: 'left 0.15s' }} />
                    </button>
                  </td>
                  <td style={{ padding: '10px 14px', fontSize: '12px', color: C.muted }}>{r.last_triggered_at ? fmtDateTime(r.last_triggered_at) : 'Never'}</td>
                  <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                    <button onClick={() => deleteRule(r)} style={{ ...btnGhost, color: C.danger, borderColor: '#F3C6C9', padding: '5px 12px', fontSize: '11.5px' }}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <SectionTitle right={
        <div style={{ display: 'flex', gap: '4px' }}>
          {(['all', 'unreviewed', 'reviewed'] as const).map(f => (
            <button key={f} onClick={() => setFilter(f)} style={{
              padding: '5px 12px', borderRadius: '3px', fontSize: '11.5px', cursor: 'pointer', fontFamily: 'system-ui', textTransform: 'capitalize',
              border: `1px solid ${filter === f ? C.navy : C.border}`, background: filter === f ? C.navy : C.white, color: filter === f ? C.white : C.textSec,
            }}>{f}</button>
          ))}
        </div>
      }>Alert History</SectionTitle>

      <div style={{ ...card, overflow: 'hidden' }}>
        {shownTriggers.length === 0 ? (
          <div style={{ padding: '28px', textAlign: 'center', fontSize: '13px', color: C.mutedLt }}>No {filter !== 'all' ? filter + ' ' : ''}alerts.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'system-ui' }}>
            <thead>
              <tr style={{ background: C.surface }}>
                {['Date', 'Firm', 'Message', 'Reviewed'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '9px 14px', fontSize: '10px', color: C.muted, fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', borderBottom: `1px solid ${C.border}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shownTriggers.map(t => (
                <tr key={t.id} style={{ borderBottom: `1px solid ${C.borderLt}` }}>
                  <td style={{ padding: '10px 14px', fontSize: '12px', color: C.muted, whiteSpace: 'nowrap' }}>{fmtDateTime(t.triggered_at)}</td>
                  <td style={{ padding: '10px 14px', fontSize: '12.5px', color: C.textSec }}>{t.firm_id ? (firmName.get(t.firm_id) || 'Unknown') : '-'}</td>
                  <td style={{ padding: '10px 14px', fontSize: '12.5px', color: C.text }}>{t.message}</td>
                  <td style={{ padding: '10px 14px' }}>
                    {t.is_reviewed
                      ? <span style={{ fontSize: '11.5px', color: C.success, fontWeight: 600 }}>✓ Reviewed</span>
                      : <button onClick={() => markReviewed(t)} style={{ ...btnGhost, padding: '5px 12px', fontSize: '11.5px' }}>Mark as Reviewed</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showAdd && <AddAlertModal firms={firms} userId={user?.id || null} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
    </div>
  );
}

function AddAlertModal({ firms, userId, onClose, onSaved }: { firms: FirmLite[]; userId: string | null; onClose: () => void; onSaved: () => void }) {
  const [alertType, setAlertType] = useState<AlertType>('firm_inactive');
  const [firmId, setFirmId] = useState<string | null>(null);
  const [threshold, setThreshold] = useState('30');
  const [saving, setSaving] = useState(false);
  const meta = ALERT_TYPES.find(a => a.value === alertType)!;

  async function save() {
    setSaving(true);
    await supabase.from('intelligence_alerts').insert({
      created_by: userId, alert_type: alertType, firm_id: firmId,
      threshold_value: meta.hasThreshold ? (parseInt(threshold, 10) || null) : null, is_active: true,
    });
    setSaving(false); onSaved();
  }

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.55)', zIndex: 100, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '60px 20px', overflowY: 'auto' }}>
      <div onClick={e => e.stopPropagation()} style={{ ...card, maxWidth: '460px', width: '100%', padding: '26px 28px' }}>
        <div style={{ fontSize: '16px', fontWeight: 700, color: C.navy, marginBottom: '18px' }}>New Alert Rule</div>
        <div style={{ marginBottom: '14px' }}>
          <label style={labelStyle}>Alert Type</label>
          <select value={alertType} onChange={e => setAlertType(e.target.value as AlertType)} style={inputStyle}>
            {ALERT_TYPES.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
          </select>
        </div>
        <div style={{ marginBottom: '14px' }}>
          <label style={labelStyle}>Firm (optional, blank = all firms)</label>
          <select value={firmId || ''} onChange={e => setFirmId(e.target.value || null)} style={inputStyle}>
            <option value="">All firms</option>
            {firms.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        </div>
        {meta.hasThreshold && (
          <div style={{ marginBottom: '14px' }}>
            <label style={labelStyle}>{meta.thresholdLabel}</label>
            <input type="number" value={threshold} onChange={e => setThreshold(e.target.value)} placeholder={meta.thresholdHint} style={inputStyle} />
          </div>
        )}
        <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
          <button onClick={onClose} style={btnGhost}>Cancel</button>
          <button onClick={save} disabled={saving} style={btnGold}>{saving ? 'Saving…' : 'Save Alert'}</button>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  TAB 5, VALUE REPORT
// ════════════════════════════════════════════════════════════════════════════
function ReportTab({ range, rangeBar, firms, people, selectedFirmId, setSelectedFirmId }: {
  range: DateRange; rangeBar: React.ReactNode; firms: FirmLite[]; people: PersonLite[];
  selectedFirmId: string | null; setSelectedFirmId: (id: string) => void;
}) {
  const { user } = useAuth();
  const firm = firms.find(f => f.id === selectedFirmId) || null;
  const memberIds = useMemo(() => people.filter(p => p.firmId === selectedFirmId).map(p => p.id), [people, selectedFirmId]);

  const [rows, setRows] = useState<ActivityRow[]>([]);
  const [detail, setDetail] = useState<FirmProductDetail | null>(null);
  const [notes, setNotes] = useState<ReportNotes | null>(null);
  const [loading, setLoading] = useState(false);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved'>('idle');
  const saveTimer = useRef<any>(null);

  const periodStart = toDateInput(range.start);
  const periodEnd = toDateInput(range.end);

  useEffect(() => {
    if (!firm) { setRows([]); setDetail(null); setNotes(null); return; }
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const [r, d, n] = await Promise.all([
          loadActivity(range, memberIds.length ? { profileIds: memberIds } : { firmId: firm.id }),
          loadFirmProductDetail(firm.id, memberIds, range),
          loadReportNotes(firm.id, periodStart, periodEnd),
        ]);
        if (!active) return;
        setRows(r); setDetail(d);
        setNotes(n || { firm_id: firm.id, period_start: periodStart, period_end: periodEnd, executive_summary: '', altconnect_notes: '', event_notes: '', talent_notes: '', service_notes: '', ai_notes: '' });
      } finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [firm, range, memberIds, periodStart, periodEnd]);

  const counts = useMemo(() => countByAction(rows), [rows]);

  function updateNote(field: keyof ReportNotes, value: string) {
    setNotes(prev => prev ? { ...prev, [field]: value } : prev);
    setSaveState('saving');
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      const cur = { ...(notes as ReportNotes), [field]: value };
      await saveReportNotes(cur, user?.id || null);
      setSaveState('saved');
      setTimeout(() => setSaveState('idle'), 1500);
    }, 800);
  }

  const noteFields: { field: keyof ReportNotes; label: string }[] = [
    { field: 'executive_summary', label: 'Executive Summary' },
    { field: 'altconnect_notes', label: 'AltConnect Activity' },
    { field: 'event_notes', label: 'Event Participation' },
    { field: 'talent_notes', label: 'Talent & Services' },
    { field: 'ai_notes', label: 'AI Usage' },
  ];

  return (
    <div>
      <style>{`@media print {
        .altveno-sidebar { display: none !important; }
        .altveno-sidebar-main { margin-left: 0 !important; }
        .ilr-noprint { display: none !important; }
        body { background: #fff !important; }
      }`}</style>

      <div className="ilr-noprint" style={{ ...card, padding: '14px 18px', marginBottom: '16px', display: 'flex', gap: '20px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: '260px' }}>
          <label style={labelStyle}>Firm</label>
          <FirmSelect firms={firms} value={selectedFirmId} onChange={setSelectedFirmId} />
        </div>
        {firm && <button onClick={() => window.print()} style={btnGold}>Generate PDF</button>}
      </div>
      {rangeBar}

      {!firm ? (
        <div style={{ ...card, padding: '48px', textAlign: 'center', fontSize: '13px', color: C.muted }}>Select a firm to build its value report.</div>
      ) : loading ? (
        <div style={{ color: C.muted, fontSize: '13px' }}>Loading report…</div>
      ) : (
        <div>
          {/* Commentary editors */}
          <div className="ilr-noprint">
            <SectionTitle right={<span style={{ fontSize: '11px', color: saveState === 'saved' ? C.success : C.mutedLt }}>{saveState === 'saving' ? 'Saving…' : saveState === 'saved' ? '✓ Saved' : 'Auto-saves'}</span>}>Commentary</SectionTitle>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '12px' }}>
              {noteFields.map(nf => (
                <div key={nf.field} style={{ ...card, padding: '14px 16px' }}>
                  <label style={labelStyle}>{nf.label}</label>
                  <textarea
                    value={(notes?.[nf.field] as string) || ''}
                    onChange={e => updateNote(nf.field, e.target.value)}
                    rows={3} placeholder={`Notes on ${nf.label.toLowerCase()}…`}
                    style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5 }} />
                </div>
              ))}
            </div>
          </div>

          {/* Report preview, this is what prints */}
          <SectionTitle>Report Preview</SectionTitle>
          <ReportDocument firm={firm} range={range} counts={counts} detail={detail} notes={notes} />
        </div>
      )}
    </div>
  );
}

function ReportDocument({ firm, range, counts, detail, notes }: {
  firm: FirmLite; range: DateRange; counts: Record<string, number>; detail: FirmProductDetail | null; notes: ReportNotes | null;
}) {
  const periodLabel = `${fmtDate(range.start)} – ${fmtDate(range.end)}`;
  const metric = (label: string, value: React.ReactNode) => (
    <div style={{ padding: '12px 14px', border: `1px solid ${C.borderLt}`, borderRadius: '4px' }}>
      <div style={{ fontFamily: 'Georgia, serif', fontSize: '24px', color: C.navy, fontWeight: 600, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: '10.5px', color: C.muted, marginTop: '5px', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{label}</div>
    </div>
  );
  const noteBlock = (label: string, text?: string | null) => text && text.trim() ? (
    <div style={{ marginBottom: '14px' }}>
      <div style={{ fontSize: '11px', fontWeight: 700, color: C.gold, letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: '4px' }}>{label}</div>
      <div style={{ fontSize: '12.5px', color: C.textSec, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{text}</div>
    </div>
  ) : null;

  return (
    <div className="ilr-report" style={{ ...card, padding: '36px 40px', maxWidth: '860px' }}>
      {/* Letterhead */}
      <div style={{ borderBottom: `2px solid ${C.navy}`, paddingBottom: '16px', marginBottom: '22px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
            <svg width="24" height="24" viewBox="0 0 28 28" fill="none">
              <path d="M 4 14 A 10 10 0 0 1 24 14" stroke={C.teal} strokeWidth="2.2" strokeLinecap="round" />
              <path d="M 6 14 A 8 8 0 0 0 22 14" stroke={C.gold} strokeWidth="2.2" strokeLinecap="round" />
              <circle cx="14" cy="14" r="2.2" fill={C.gold} />
            </svg>
            <span style={{ fontFamily: 'Georgia, serif', fontSize: '20px', color: C.navy }}>Altveno</span>
          </div>
          <div style={{ fontSize: '10px', color: C.gold, letterSpacing: '1.5px', fontWeight: 600, textTransform: 'uppercase' }}>Value Delivered Report</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '15px', fontWeight: 700, color: C.navy }}>{firm.name}</div>
          <div style={{ fontSize: '11.5px', color: C.muted }}>{periodLabel}</div>
        </div>
      </div>

      {noteBlock('Executive Summary', notes?.executive_summary)}

      <div style={{ fontSize: '11px', fontWeight: 700, color: C.navy, letterSpacing: '0.5px', textTransform: 'uppercase', margin: '20px 0 10px' }}>Activity Snapshot</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '20px' }}>
        {metric('Total actions', fmtNumber(counts ? Object.values(counts).reduce((a, b) => a + b, 0) : 0))}
        {metric('Connections', fmtNumber((counts['connection_requested'] || 0) + (detail?.connectionsApproved || 0)))}
        {metric('Events', fmtNumber(detail?.eventsRegistered || 0))}
        {metric('AI queries', fmtNumber(counts['ai_query_made'] || 0))}
        {metric('Investments', fmtNumber(detail?.invested || 0))}
        {metric('Docs shared', fmtNumber(detail?.docsAccessedByAllocators || 0))}
        {metric('Surveys done', fmtNumber(detail?.surveysCompleted || 0))}
        {metric('DDQs', fmtNumber(detail?.ddqSubmitted || 0))}
      </div>

      {noteBlock('AltConnect Activity', notes?.altconnect_notes)}
      {noteBlock('Event Participation', notes?.event_notes)}
      {noteBlock('Talent & Services', notes?.talent_notes)}
      {noteBlock('AI Usage', notes?.ai_notes)}

      <div style={{ marginTop: '28px', paddingTop: '14px', borderTop: `1px solid ${C.borderLt}`, fontSize: '10.5px', color: C.mutedLt, textAlign: 'center' }}>
        Generated by Altveno Intelligence · {fmtDate(new Date())} · Confidential
      </div>
    </div>
  );
}
